import enUS from '../help-content/en-US.json';
import en from '../help-content/en.json';
import esES from '../help-content/es-ES.json';
import es419 from '../help-content/es-419.json';
import de from '../help-content/de.json';
import fr from '../help-content/fr.json';
import nl from '../help-content/nl.json';
import fi from '../help-content/fi.json';
import hi from '../help-content/hi.json';
import sv from '../help-content/sv.json';

export interface HelpContentItem {
  title: string;
  description: string;
  instructions: string[];
  tips: string[];
}

export type HelpCatalog = Record<string, HelpContentItem>;

export const catalogs: Record<string, HelpCatalog> = {
  'en-US': enUS,
  en: en,
  'es-ES': esES,
  'es-419': es419,
  de: de,
  fr: fr,
  nl: nl,
  fi: fi,
  hi: hi,
  sv: sv,
};

/**
 * Resolves localized help content for a given URL pathname and language.
 * Follows a cascade:
 * 1. Targeted localized catalog (e.g. 'es-ES', 'de', 'fr')
 * 2. Base language fallback (e.g. 'es-MX' -> 'es-419' -> 'es-ES')
 * 3. Canonical 'en-US' fallback
 * 4. Empty-state fallback
 * 
 * Uses longest prefix matching for nested tool routes.
 */
export function getHelpContent(pathname: string, language?: string): HelpContentItem {
  const langKey = language || 'en-US';

  // 1. Resolve primary catalog
  let catalog = catalogs[langKey];
  if (!catalog) {
    const base = langKey.split('-')[0].toLowerCase();
    if (base === 'es') {
      catalog = catalogs['es-ES'] || catalogs['es-419'];
    } else if (base === 'en') {
      catalog = catalogs['en-US'];
    } else {
      catalog = catalogs[base] || catalogs['en-US'];
    }
  }

  const fallbackCatalog = catalogs['en-US'];

  const resolveRoute = (cat: HelpCatalog, path: string): HelpContentItem | undefined => {
    if (!cat) return undefined;
    if (cat[path]) return cat[path];

    // Strip trailing slashes
    const normalized = path.replace(/\/+$/, '');
    if (cat[normalized]) return cat[normalized];

    // Longest prefix match
    const sortedKeys = Object.keys(cat).sort((a, b) => b.length - a.length);
    const matchedKey = sortedKeys.find(key => path === key || path.startsWith(`${key}/`));
    if (matchedKey) return cat[matchedKey];

    return undefined;
  };

  const item = resolveRoute(catalog, pathname) || resolveRoute(fallbackCatalog, pathname);

  if (item) {
    return item;
  }

  return {
    title: "Help",
    description: "No help content available for this page.",
    instructions: [],
    tips: []
  };
}

export default getHelpContent;
